import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import CustomTooltip from '../../../../components/common/CustomTooltip';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import dayjs, { Dayjs } from 'dayjs';
import { ScheduleCard } from './style';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'store';
import { fetchFacilityShiftCalendar } from 'modules/facility/actions';
import { selectFacilityShiftCalendar } from 'modules/facility/selectors';
import { IFacilityShiftCalendar } from 'modules/facility/types';

const ShiftSchedule: React.FC = () => {
  const [currentDate, setCurrentDate] = useState<Dayjs>(dayjs());
  const dispatch = useDispatch<AppDispatch>();
  const shiftCalendar: IFacilityShiftCalendar | null = useSelector(selectFacilityShiftCalendar);

  useEffect(() => {
    dispatch(fetchFacilityShiftCalendar({
      year: currentDate.year(),
      month: currentDate.month() + 1 // dayjs month is 0-indexed
    }));
  }, [dispatch, currentDate]);

  const handlePrevMonth = () => {
    setCurrentDate(currentDate.subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setCurrentDate(currentDate.add(1, 'month'));
  };

  const days = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  // Generate dates
  const startOfMonth = currentDate.startOf('month');
  const endOfMonth = currentDate.endOf('month');
  const startDate = startOfMonth.startOf('week');
  const endDate = endOfMonth.endOf('week');

  const calendarData = [];
  let day = startDate;

  const getShiftData = (date: Dayjs) => {
    if (!shiftCalendar) return null;

    const dateKey = date.format('YYYY-MM-DD');
    const dayData = shiftCalendar[dateKey];

    if (!dayData) return null;

    const { status_counts } = dayData;

    // Define Buckets
    const buckets = [
      { name: 'cancelled', color: '#ef9a9a', textColor: '#c62828', keys: ['cancelled'], count: 0 },
      { name: 'active', color: '#a5d6a7', textColor: '#fff', keys: ['active', 'started', 'partially_started'], count: 0 },
      { name: 'upcoming', color: '#e3f2fd', textColor: '#1565c0', keys: ['assigned', 'upcoming', 'partially_assigned'], count: 0 },
      { name: 'pending', color: '#fff9c4', textColor: '#f9a825', keys: ['pending', 'unfilled', 'scheduled'], count: 0 },
      { name: 'completed', color: '#f5f5f5', textColor: '#616161', keys: ['completed'], count: 0 }
    ];

    // Aggregate counts
    Object.entries(status_counts).forEach(([key, value]) => {
      const bucket = buckets.find(b => b.keys.includes(key));
      if (bucket) {
        bucket.count += value;
      } else {
        // Fallback for unknown keys, maybe to pending or just ignore? 
        // For now, let's map unknown to pending or create a 'other' bucket? 
        // Given the requirement, let's stick to defined buckets.
      }
    });

    const activeBuckets = buckets.filter(b => b.count > 0);

    // If only one bucket has data, show standard "X shifts" label
    if (activeBuckets.length === 1) {
      const bucket = activeBuckets[0];
      // Use total_shifts for label count to be safe, or bucket.count. 
      // Usually bucket.count should equal total_shifts if all keys are mapped. 
      // Let's use bucket.count for consistency with the color.
      const label = `${bucket.count} shift${bucket.count !== 1 ? 's' : ''}`;
      return {
        items: [{ label, display: label, color: bucket.color, textColor: bucket.textColor, count: bucket.count }]
      };
    }

    // If multiple buckets, return list of badges with just count
    if (activeBuckets.length > 1) {
      return {
        items: activeBuckets.map(b => ({
          label: `${b.count} ${b.name}`,
          display: b.count.toString(),
          color: b.color,
          textColor: b.textColor,
          count: b.count
        }))
      };
    }

    return null;
  };

  while (day.isBefore(endDate.add(1, 'day'))) {
    calendarData.push({
      date: day,
      isCurrentMonth: day.month() === currentDate.month(),
      data: getShiftData(day)
    });
    day = day.add(1, 'day');
  }

  return (
    <ScheduleCard>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Shift Schedule</Typography>

      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '12px', p: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <IconButton onClick={handlePrevMonth} size="small" sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', p: 1 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            {currentDate.format('MMMM, YYYY')}
          </Typography>
          <IconButton onClick={handleNextMonth} size="small" sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', p: 1 }}>
            <ArrowForwardIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Days Header */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, mb: 1 }}>
          {days.map(dayName => (
            <Box key={dayName} sx={{ textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: '#9e9e9e', fontWeight: 600 }}>
                {dayName}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Calendar Grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, rowGap: 3 }}>
          {calendarData.map((item, i) => (
            <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minHeight: 40,
                opacity: item.isCurrentMonth ? 1 : 0.3,
                width: '100%'
              }}>
                <Typography variant="body1" sx={{
                  fontWeight: 600,
                  color: '#424242',
                  mb: 0.5
                }}>
                  {item.date.date()}
                </Typography>

                {item.data && (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center', width: '100%' }}>
                    {item.data.items.map((badge, idx, array) => (
                      <CustomTooltip key={idx} title={badge.label} arrow placement="top">
                        <Box sx={{
                          bgcolor: badge.color,
                          px: 1,
                          py: 0.25,
                          borderRadius: '4px',
                          minWidth: array.length > 1 ? 'auto' : '95%',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          cursor: 'pointer'
                        }}>
                          <Typography variant="caption" sx={{
                            fontSize: '0.65rem',
                            color: badge.textColor,
                            fontWeight: 600,
                            whiteSpace: 'nowrap'
                          }}>
                            {badge.display || badge.label}
                          </Typography>
                        </Box>
                      </CustomTooltip>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Legend */}
      <Box sx={{ mt: 3, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: '#9e9e9e' }} />
          <Typography variant="body2" color="textPrimary">Completed</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: '#4caf50' }} />
          <Typography variant="body2" color="textPrimary">Active</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: '#42a5f5' }} />
          <Typography variant="body2" color="textPrimary">Upcoming</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: '#fdd835' }} />
          <Typography variant="body2" color="textPrimary">Pending</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: '#ef5350' }} />
          <Typography variant="body2" color="textPrimary">Cancelled</Typography>
        </Box>
      </Box>
    </ScheduleCard>
  );
};

export default ShiftSchedule;
